import {
  buildScoutInternalEventMessage,
  parseRespondedScoutInternalEventsFromMessages,
  type ChatMessageLike,
  type ScoutInternalEventStatus,
} from "./scoutInternalEvents";

export type DataChangeProposalEventStatus = ScoutInternalEventStatus;

export type DataChangeProposalEventItem = {
  proposalId: string;
  /** Short human-readable label for the change (card title). */
  title: string;
  status: DataChangeProposalEventStatus;
};

function buildConfirmedDetail(title: string, proposalId: string): string {
  return (
    `User accepted ${title} (proposalId=${proposalId}). ` +
    `Call execute_graphql with approvedProposalId=${proposalId} to apply the exact staged mutation. ` +
    `Do not re-propose the same mutation. After the apply tool result succeeds, confirm what changed in prose.`
  );
}

function buildDeclinedDetail(title: string): string {
  return (
    `Rejected ${title}. ` +
    `Do not call execute_graphql with this proposalId. Adapt or ask how the user wants to proceed; ` +
    `do not re-propose the same mutation unless they ask.`
  );
}

/**
 * Build the synthetic user message for one or more data-change resolutions.
 * Multiple items become multiple `[scout-internal-event]` lines in one
 * message so Accept all / Reject all / mixed per-card decisions trigger a
 * single Scout turn while each proposalId rehydrates independently from
 * history. Accept does not apply GraphQL — the agent must execute with
 * approvedProposalId.
 */
export function buildDataChangeProposalEventMessage(input: {
  proposals: DataChangeProposalEventItem[];
}): string {
  if (input.proposals.length === 0) {
    return "";
  }

  return input.proposals
    .map((proposal) => {
      if (proposal.status === "confirmed") {
        return buildScoutInternalEventMessage({
          kind: "data-change-proposal",
          status: "confirmed",
          proposalId: proposal.proposalId,
          detail: buildConfirmedDetail(proposal.title, proposal.proposalId),
        });
      }
      return buildScoutInternalEventMessage({
        kind: "data-change-proposal",
        status: "declined",
        proposalId: proposal.proposalId,
        detail: buildDeclinedDetail(proposal.title),
      });
    })
    .join("\n");
}

/**
 * Scan persisted history for data-change-proposal events and return
 * `proposalId -> status`.
 */
export function parseRespondedDataChangeProposalsFromMessages(
  messages: ReadonlyArray<ChatMessageLike>
): Record<string, DataChangeProposalEventStatus> {
  return parseRespondedScoutInternalEventsFromMessages(messages, "data-change-proposal");
}
